import React, { useState, useEffect } from "react";
import {
  TextInput,
  StyleSheet,
  TextInputProps,
  View,
  Text,
  ViewStyle,
  Image,
  TouchableOpacity,
} from "react-native";
import { useColors } from "../../hooks/useColors";
import { Ionicons } from "@expo/vector-icons";

export interface InputProps extends Omit<TextInputProps, "style"> {
  label?: string;
  error?: string;
  style?: ViewStyle;
  required?: boolean;
  rightIcon?: React.ReactNode;
  isPassword?: boolean;
  setPaswordVisibility?: (visible: boolean) => void;
  isPasswordVisible?: boolean;
  leftIcon?: any; // Image source for left icon
  formatPhone?: boolean; // Automatically formats phone numbers as (XXX) XXX-XXXX and returns numeric value to onChangeText
  iconName?: string; // Name of icon to display on the left
  autoCapitalize?: "none" | "sentences" | "words" | "characters";
  autoCorrect?: boolean;
}

const EYE_ICON = require("../../assets/eye.png");
const EYE_HIDDEN_ICON = require("../../assets/eye-hidden.png");

// Phone number formatting utilities
const formatPhoneNumber = (value: string): string => {
  // Remove all non-numeric characters
  const numericOnly = value.replace(/\D/g, "");

  // Limit to 10 digits for US phone numbers
  const limited = numericOnly.slice(0, 10);

  // Format as (XXX) XXX-XXXX
  if (limited.length === 0) return "";
  if (limited.length <= 3) return `(${limited}`;
  if (limited.length <= 6)
    return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
  return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
};

const getNumericValue = (formattedValue: string): string => {
  return formattedValue.replace(/\D/g, "");
};

const createInputStyles = (colors: any) =>
  StyleSheet.create({
    container: {
      marginBottom: 0,
    },
    label: {
      marginBottom: 8,
      fontSize: 14,
      fontWeight: "500",
      color: colors.text,
    },
    inputContainer: {
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      flexDirection: "row",
    },
    input: {
      padding: 12,
      fontSize: 16,
      flex: 1,
      color: colors.text,
    },
    inputError: {
      borderColor: colors.error,
    },
    error: {
      color: colors.error,
      fontSize: 12,
    },
    rightIcon: {
      position: "absolute",
      right: 12,
      top: 12,
    },
    passwordIcon: {
      width: 24,
      height: 24,
    },
    leftIcon: {
      width: 24,
      height: 24,
      left: 12,
      top: 12,
      marginRight: 8,
    },
  });

export function Input({
  label,
  error,
  style,
  isPassword,
  setPaswordVisibility,
  isPasswordVisible = false,
  leftIcon,
  formatPhone = false,
  onChangeText,
  value,
  iconName,
  autoCapitalize = "sentences",
  autoCorrect = true,
  ...props
}: InputProps) {
  const colors = useColors();
  const styles = createInputStyles(colors);
  const [displayValue, setDisplayValue] = useState(value || "");

  const switchPasswordVisibility = () => {
    if (setPaswordVisibility) {
      setPaswordVisibility(!isPasswordVisible);
    }
  };

  const handleTextChange = (text: string) => {
    if (formatPhone) {
      // Format the phone number for display
      const formatted = formatPhoneNumber(text);
      setDisplayValue(formatted);

      // Pass the numeric-only value to parent component
      const numericValue = getNumericValue(formatted);
      if (onChangeText) {
        onChangeText(numericValue);
      }
    } else {
      setDisplayValue(text);
      if (onChangeText) {
        onChangeText(text);
      }
    }
  };

  // Update display value when value prop changes
  useEffect(() => {
    if (value !== undefined) {
      if (formatPhone && value) {
        setDisplayValue(formatPhoneNumber(value));
      } else {
        setDisplayValue(value || "");
      }
    }
  }, [value, formatPhone]);

  if (isPassword) {
    console.log("isPasswordVisible", isPassword && isPasswordVisible);
  }

  return (
    <View style={[styles.container, style]}>
      {label && <Text style={styles.label}>{label}</Text>}
      <View style={[styles.inputContainer, error ? styles.inputError : null]}>
        {leftIcon && (
          <View style={styles.leftIcon}>
            <Image source={leftIcon} style={styles.passwordIcon} />
          </View>
        )}
        {iconName && (
          <Ionicons
            name={iconName}
            size={24}
            color={colors.textLight}
            style={styles.leftIcon}
          />
        )}
        <TextInput
          style={styles.input}
          placeholderTextColor={colors.textLight}
          secureTextEntry={isPassword && isPasswordVisible}
          value={displayValue}
          onChangeText={handleTextChange}
          keyboardType={formatPhone ? "phone-pad" : props.keyboardType}
          maxLength={formatPhone ? 14 : props.maxLength} // (XXX) XXX-XXXX = 14 chars
          autoCapitalize={autoCapitalize}
          autoCorrect={autoCorrect}
          textContentType={isPassword ? "none" : props.textContentType}
          autoComplete={isPassword ? "off" : props.autoComplete}
          {...props}
        />
        {isPassword && (
          <TouchableOpacity
            style={styles.rightIcon}
            onPress={switchPasswordVisibility}
          >
            {isPasswordVisible ? (
              <Ionicons
                name="eye-off-outline"
                size={24}
                color={colors.textLight}
                style={styles.passwordIcon}
              />
            ) : (
              <Ionicons
                name="eye-outline"
                size={24}
                color={colors.textLight}
                style={styles.passwordIcon}
              />
            )}
          </TouchableOpacity>
        )}
      </View>
      {error && <Text style={styles.error}>{error}</Text>}
    </View>
  );
}
